import { toAttributesInput } from '@shared/shop-api/attributes/mapper'
import type {
  CartLineRequestAttributes,
  CartLineUpdateRequestAttributes,
} from '@shared/shop-api/cart/lines/types'
import { toCart } from '@shared/shop-api/cart/mapper'
import {
  addToCartQuery,
  cartCreateQuery,
  getCartQuery,
  removeFromCartQuery,
  updateCartLinesQuery,
} from '@shared/shop-api/cart/query'
import type { CartRequestAttributes } from '@shared/shop-api/cart/types'
import { StorefrontError } from '@shared/shop-api/core/error'
import { isClientData } from '@shared/shop-api/core/helper'
import { execute } from '@shared/shop-api/core/storefront'

export const createCart = async (input?: CartRequestAttributes) => {
  const cartData = await execute(cartCreateQuery, {
    input: {
      lines: input?.lines?.map((line) => ({
        merchandiseId: `gid://shopify/ProductVariant/${line.variantId}`,
        quantity: line.quantity ?? 1,
      })),
      note: input?.note,
      delivery: {
        addresses: input?.deliveryAddress
          ? [
              {
                address: {
                  deliveryAddress: {
                    address1: input.deliveryAddress.address1,
                    city: input.deliveryAddress.city,
                    firstName: input.deliveryAddress.firstName,
                    lastName: input.deliveryAddress.lastName,
                    phone: input.deliveryAddress.phone,
                    zip: input.deliveryAddress.zipCode,
                    countryCode: input.deliveryAddress.iso2Code,
                  },
                },
                oneTimeUse: false,
                selected: true,
              },
            ]
          : [],
      },
    },
  })

  if (!isClientData(cartData) || !cartData.cartCreate.cart) {
    throw new StorefrontError('Cart creation failed', cartData)
  }

  return toCart(cartData.cartCreate.cart, cartData.cartCreate.warnings)
}

export const getCart = async (cartId: string) => {
  const cartData = await execute(getCartQuery, {
    cartId,
  })

  if (!isClientData(cartData) || !cartData.cart) {
    throw new StorefrontError('get Cart failed', cartData)
  }

  return toCart(cartData.cart)
}

export const addToCart = async (
  cartId: string,
  lines: CartLineRequestAttributes[]
) => {
  const cartData = await execute(addToCartQuery, {
    cartId,
    lines: lines.map((line) => ({
      merchandiseId: `gid://shopify/ProductVariant/${line.variantId}`,
      quantity: line.quantity ?? 1,
      attributes: toAttributesInput(line.attributes || {}),
    })),
  })

  if (!isClientData(cartData) || !cartData.cartLinesAdd.cart) {
    throw new StorefrontError('Add to cart failed', cartData)
  }

  return toCart(cartData.cartLinesAdd.cart, cartData.cartLinesAdd.warnings)
}

export const removeFromCart = async (cartId: string, lineIds: string[]) => {
  const cartData = await execute(removeFromCartQuery, {
    cartId,
    lineIds,
  })

  if (!isClientData(cartData) || !cartData.cartLinesRemove.cart) {
    throw new StorefrontError('Remove from cart failed', cartData)
  }

  return toCart(
    cartData.cartLinesRemove.cart,
    cartData.cartLinesRemove.warnings
  )
}

export const updateCartLines = async (
  cartId: string,
  lines: CartLineUpdateRequestAttributes[]
) => {
  const cartData = await execute(updateCartLinesQuery, {
    cartId,
    lines: lines.map((line) => ({
      ...line,
      attributes: toAttributesInput(line.attributes || {}),
    })),
  })

  if (!isClientData(cartData) || !cartData.cartLinesUpdate.cart) {
    throw new StorefrontError('Update cart lines failed', cartData)
  }

  return toCart(
    cartData.cartLinesUpdate.cart,
    cartData.cartLinesUpdate.warnings
  )
}
